/**
 * The money-state vocabulary for every billing surface — program plan §11's
 * binding table, in one place so the billing tab, the payments-due dashboard,
 * and the composer cannot drift into three dialects of the same state.
 *
 * THE RULE THIS FILE EXISTS TO KEEP: colour is never the only signal. Every
 * state carries an ink text label; the colour is a second, redundant channel.
 * Green and amber never render as small coloured text (the AA learning of
 * 2026-07-25) — they appear as a dot, a ring, or a tinted wash behind ink text.
 * Brick red is reserved for destructive buttons; a state is never red, because
 * a parent being late is not an error.
 */
import React from 'react';
import { Box, Chip, Typography } from '@mui/material';

import type { ReceiptAction } from '@shared/paymentReceipts';
import { C } from '@/components/adaptive-picker/adaptiveTokens';
import { WORKSPACE_TARGET_MIN_HEIGHT } from '@/components/common/targetSizes';
import type {
  BalanceStatus,
  BillingCycleState,
  BillingPaymentRow,
  BillingPaymentSource,
} from '@/services/studentBillingApi';

/** Tabular figures for every money and count column (DESIGN.md §5). */
export const TNUM_SX = { fontFeatureSettings: "'tnum'" } as const;

/**
 * Amber wash, in one place.
 *
 * DESIGN.md §4 defines Amber `#F0A880` and Amber-dark `#D18B63`, but the shared
 * `C` token object never carried them, so every amber surface in this feature
 * would otherwise hand-type its own rgba — and the first draft of this file
 * already had four different alphas for what is meant to read as one state.
 * Two strengths is the whole vocabulary: `strong` for the worst state on a
 * surface, `soft` for its lesser sibling.
 */
export const AMBER = {
  strongBg: 'rgba(240, 168, 128, 0.22)',
  strongBorder: 'rgba(209, 139, 99, 0.55)',
  softBg: 'rgba(240, 168, 128, 0.12)',
  softBorder: 'rgba(209, 139, 99, 0.42)',
} as const;

/** "$200" — whole dollars stay whole, matching the bill message. */
export function formatMoney(amount: number): string {
  const cents = Math.round(amount * 100);
  return cents % 100 === 0 ? `$${cents / 100}` : `$${(cents / 100).toFixed(2)}`;
}

/** "Sat 2 Aug" — the same reading the parent gets in their message. */
export function formatSessionDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Singapore',
  })
    .format(date)
    .replace(',', '');
}

/**
 * "Sun 22 Feb, 2:18 am" — for a payment, where two of the same amount can land
 * on one day and the date alone would read as one payment listed twice.
 */
export function formatPaymentMoment(iso: string | null | undefined): string {
  const day = formatSessionDate(iso);
  if (day === '—' || !iso) return day;
  const time = new Intl.DateTimeFormat('en-GB', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'Asia/Singapore',
  }).format(new Date(iso));
  return `${day}, ${time}`;
}

/** "2d ago" / "just now" — the reminder memory on a payments-due group. */
export function formatRelativeDays(iso: string | null | undefined): string {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const days = Math.floor((Date.now() - then) / 86_400_000);
  if (days <= 0) return 'just now';
  return days === 1 ? '1d ago' : `${days}d ago`;
}

// ---------------------------------------------------------------------------
// Cycle state
// ---------------------------------------------------------------------------

interface CycleStatePresentation {
  label: string;
  /** Wash behind ink text, never coloured text on its own. */
  background: string;
  border: string;
}

const CYCLE_STATE: Record<BillingCycleState, CycleStatePresentation> = {
  // Amber-wash + a day count, never red: an overdue parent is a nudge, not an
  // error state.
  overdue: {
    label: 'Overdue',
    background: AMBER.strongBg,
    border: AMBER.strongBorder,
  },
  due: {
    label: 'Due',
    background: AMBER.softBg,
    border: AMBER.softBorder,
  },
  'in-progress': {
    label: 'In progress',
    background: C.bgAlt,
    border: C.border,
  },
  'paid-ahead': {
    label: 'Paid ahead',
    background: 'rgba(46, 163, 102, 0.10)',
    border: 'rgba(46, 163, 102, 0.35)',
  },
};

/**
 * The cycle's state, and — past one cycle late — how far past.
 *
 * A day count is a reading anyone can act on for a week or two. At "97d" it is
 * noise; "3 cycles behind" is the sentence a human actually needs, so from two
 * cycles the label switches and the days drop rather than stacking both.
 *
 * The escalation is the LABEL and the sort position, never a third amber. This
 * file's two-strength rule is the whole vocabulary (and a state is never red —
 * a parent being late is not an error), so a deeper debt reads as worse by what
 * it says and by where it sits, not by getting louder.
 */
export const CycleStateChip: React.FC<{
  state: BillingCycleState;
  daysOverdue?: number;
  cyclesBehind?: number;
}> = ({ state, daysOverdue = 0, cyclesBehind = 0 }) => {
  const presentation = CYCLE_STATE[state];
  const label =
    cyclesBehind >= 2
      ? `${cyclesBehind} cycles behind`
      : state === 'overdue' && daysOverdue > 0
        ? `${presentation.label} · ${daysOverdue}d`
        : presentation.label;
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: presentation.background,
        border: `1px solid ${presentation.border}`,
        color: C.ink,
        fontWeight: 700,
        ...TNUM_SX,
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Balance status
// ---------------------------------------------------------------------------

const BALANCE_LABELS: Record<BalanceStatus, string> = {
  on_break: 'On break',
  unpaid: 'Unpaid lessons',
  due: 'Payment due',
  low: 'Low credits',
  paid: 'Paid lessons available',
  needs_attention: 'Needs checking',
  settled: 'Settled',
};

/**
 * A class balance's state, readable down a list without reading the figures.
 *
 * Owing and checking are exceptions, so they wear the amber wash, strong for
 * lessons already taught and unpaid. Paid and settled are settlement, so a
 * green dot beside ink text (DESIGN.md §15), never a green label.
 */
export const BalanceStatusChip: React.FC<{ status: BalanceStatus }> = ({
  status,
}) => {
  const settled =
    status === 'paid' || status === 'settled' || status === 'on_break';
  return (
    <Chip
      size="small"
      label={BALANCE_LABELS[status]}
      icon={
        settled ? (
          <Box
            aria-hidden
            sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: C.green }}
          />
        ) : undefined
      }
      sx={{
        bgcolor: settled
          ? 'transparent'
          : status === 'unpaid'
            ? AMBER.strongBg
            : AMBER.softBg,
        border: `1px solid ${
          settled
            ? C.border
            : status === 'unpaid'
              ? AMBER.strongBorder
              : AMBER.softBorder
        }`,
        color: C.ink,
        fontWeight: 700,
        '& .MuiChip-icon': { ml: 1, mr: -0.25 },
      }}
    />
  );
};

/**
 * "They left, this is not finished." Deliberately neutral — grey, not red: the
 * urgency belongs to the cycle-state chip next to it, and colouring this one
 * would say the leaving is the problem rather than the open balance.
 */
export const FormerMemberChip: React.FC<{
  leftAt?: string | null;
  /** "Former class" on a line that names the class itself. */
  label?: string;
}> = ({ leftAt, label = 'Former member' }) => (
  <Chip
    size="small"
    label={leftAt ? `${label} · left ${formatSessionDate(leftAt)}` : label}
    sx={{
      bgcolor: 'transparent',
      border: `1px solid ${C.border}`,
      color: C.inkMuted,
      fontWeight: 700,
    }}
  />
);

/**
 * Where a student stands, beside their name in the record's head. A chip
 * beside a name always means "look": a submitted applicant wears the amber
 * wash, the waiting and the gone wear the former-member face, and a verified
 * student — the ordinary case — gets no chip at all, only the muted word
 * {@link VerifiedWord}. The roster row shows only the chips no other cell
 * tells (on hold, former, rejected): the Billing phrase and the Verify button
 * say which applicant is which, so the name keeps its width. Green belongs
 * to the money column alone.
 */
const ENROLLMENT_STATUS_LABELS: Record<string, string> = {
  PENDING_PAYMENT: 'Pending',
  PAYMENT_SUBMITTED: 'Submitted',
  ON_HOLD: 'On hold',
  FORMER: 'Former',
  REJECTED: 'Rejected',
};

/** The face both kinds of status chip share: 22px, 12px/700, a pill. */
const STATUS_FACE_SX = {
  height: 22,
  fontSize: 12,
  fontWeight: 700,
  '& .MuiChip-label': { px: '8px' },
} as const;

export const EnrollmentStatusChip: React.FC<{ status: string }> = ({
  status,
}) => {
  const label = ENROLLMENT_STATUS_LABELS[status];
  if (!label) return null;
  const waiting = status === 'PAYMENT_SUBMITTED';
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        ...STATUS_FACE_SX,
        bgcolor: waiting ? AMBER.softBg : 'transparent',
        border: `1px solid ${waiting ? AMBER.softBorder : C.border}`,
        color: waiting ? C.ink : C.inkMuted,
      }}
    />
  );
};

/** A verified student's status: a muted word, never a chip (R11). */
export const VerifiedWord: React.FC = () => (
  <Typography
    component="span"
    sx={{ fontSize: 12, fontWeight: 700, color: C.inkMuted }}
  >
    Verified
  </Typography>
);

/** Account context only — the cycle-state chip carries the balance's urgency. */
export const TrialAccountChip: React.FC = () => (
  <Chip
    size="small"
    label="Trial account"
    sx={{
      bgcolor: 'transparent',
      border: `1px solid ${C.border}`,
      color: C.inkMuted,
      fontWeight: 700,
    }}
  />
);

/**
 * "This class's lessons are paid for over there."
 *
 * Cobalt-washed rather than amber or neutral: a shared payment pool is a
 * deliberate arrangement, not a state anyone has to fix, and cobalt is this
 * codebase's navigation colour — which is what this is. It sits in the ACTION
 * row and takes the place of Record payment / Set opening balance, because the
 * only useful thing to do from a pooled row is go to the class holding the
 * money, and it is sized like its button neighbours so it is a real target
 * rather than a 24px decoration.
 */
export const BilledUnderChip: React.FC<{
  classCode: string;
  onOpen?: () => void;
}> = ({ classCode, onOpen }) => (
  <Chip
    {...(onOpen ? { component: 'button' as const, onClick: onOpen } : {})}
    label={`Billed under ${classCode}`}
    sx={{
      height: WORKSPACE_TARGET_MIN_HEIGHT,
      bgcolor: C.cobaltWash,
      border: `1px solid ${C.border}`,
      color: C.ink,
      fontWeight: 700,
      ...(onOpen ? { cursor: 'pointer' } : {}),
    }}
  />
);

// ---------------------------------------------------------------------------
// Payment state
// ---------------------------------------------------------------------------

/**
 * Verified / reported-unverified / voided, per §11. A voided row keeps its
 * chip: the whole point of the soft void is that the reversal stays visible.
 */
export const PaymentStateChip: React.FC<{ payment: BillingPaymentRow }> = ({
  payment,
}) => {
  if (payment.status === 'historically_settled') {
    return (
      <Typography component="span" sx={{ fontSize: 12, color: C.inkMuted }}>
        Settled previously
      </Typography>
    );
  }
  if (payment.status === 'voided') {
    return (
      <Chip
        size="small"
        label="Voided"
        sx={{
          bgcolor: 'transparent',
          border: `1px dashed ${C.border}`,
          color: C.inkMuted,
          fontWeight: 700,
        }}
      />
    );
  }
  if (payment.status === 'pending') {
    return (
      <Chip
        size="small"
        label="Not paid yet"
        variant="outlined"
        sx={{ color: C.inkMuted, borderColor: C.border, fontWeight: 700 }}
      />
    );
  }
  if (payment.confirmedBy === 'parent') {
    return (
      <Chip
        size="small"
        label="Reported — unverified"
        sx={{
          bgcolor: AMBER.softBg,
          border: `1px solid ${AMBER.softBorder}`,
          color: C.ink,
          fontWeight: 700,
        }}
      />
    );
  }
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        aria-hidden
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          bgcolor: C.green,
          flexShrink: 0,
        }}
      />
      <Typography
        component="span"
        sx={{ fontSize: 12, fontWeight: 700, color: C.ink }}
      >
        Verified
      </Typography>
    </Box>
  );
};

/**
 * The two fields that decide what a row IS, structurally — so the enrollment
 * payments table, whose row DTO is its own narrower shape, can label its rows
 * the same way the billing tab does instead of growing a second dialect.
 */
export interface PaymentSourceFields {
  source?: BillingPaymentSource;
  slotsCovered?: number | null;
}

/** A state chip in the amber wash with ink text (DESIGN.md §4). */
export const AmberChip: React.FC<{ label: string; strong?: boolean }> = ({
  label,
  strong = false,
}) => (
  <Chip
    size="small"
    label={label}
    sx={{
      bgcolor: strong ? AMBER.strongBg : AMBER.softBg,
      border: `1px solid ${strong ? AMBER.strongBorder : AMBER.softBorder}`,
      color: 'var(--ink)',
      fontWeight: 700,
    }}
  />
);

/** A quiet outlined chip for a fact that asks nothing of the admin. */
export const GhostChip: React.FC<{ label: string }> = ({ label }) => (
  <Chip
    size="small"
    label={label}
    sx={{
      bgcolor: 'transparent',
      border: '1px solid var(--border)',
      color: 'var(--ink-muted)',
      fontWeight: 700,
    }}
  />
);

/** Where a row came from, when that is not simply "a payment". */
export function paymentSourceLabel(
  payment: PaymentSourceFields
): string | null {
  if (payment.source === 'enrollment_seed') return 'Enrollment seed';
  if (payment.source === 'cutover_seed') return 'Opening balance';
  if (payment.source === 'value_conversion')
    return 'Value conversion · no cash received';
  if (payment.source === 'value_adjustment')
    return 'Value adjustment · no cash received';
  // The sign says which end of the transfer this row is. Both legs carry the
  // other class's code in their notes, which the row already renders — so the
  // label states the direction and lets the notes name the class.
  if (payment.source === 'carry_over') {
    return (payment.slotsCovered ?? 0) < 0
      ? 'Balance carried out'
      : 'Balance carried in';
  }
  return null;
}

// ---------------------------------------------------------------------------
// Receipts
// ---------------------------------------------------------------------------

/**
 * What the receipt button on a row says.
 *
 * Two words apart, and the difference is the whole safety story: issuing mints
 * a number that is kept forever, downloading re-prints a document a parent
 * already has. `receiptActionFor` decides WHICH; this decides how it reads, so
 * the enrollments panel's icon label and the billing card's button can never
 * describe the same act in two voices.
 */
export function receiptActionLabel(action: ReceiptAction): string {
  return action === 'issue' ? 'Issue receipt' : 'Download receipt';
}

/** The two fields a caption needs — every row DTO carries them optionally. */
export interface ReceiptCaptionFields {
  receiptNumber?: string | null;
  receiptIssuedAt?: string | null;
}

/**
 * "Receipt DT-20260828-001 · issued Fri 28 Aug" — null when none was issued.
 *
 * The NUMBER leads, because that is what a parent quotes back down the phone.
 * The issue date follows it rather than replacing the row's own payment date:
 * a backdated receipt's number already carries the receipt's date, so this line
 * answers the other question ("when did we send it?") and the two never collide.
 */
export function receiptCaption(row: ReceiptCaptionFields): string | null {
  if (!row.receiptNumber) return null;
  const issued = formatSessionDate(row.receiptIssuedAt);
  return issued === '—'
    ? `Receipt ${row.receiptNumber}`
    : `Receipt ${row.receiptNumber} · issued ${issued}`;
}

// ---------------------------------------------------------------------------
// Reminder state
// ---------------------------------------------------------------------------

/**
 * "Somebody has already chased this parent" — the pill that tells a handled row
 * from an untouched one across a list, which two identically-muted 12px lines
 * never did.
 *
 * Cobalt, never green: §15 anchors green to SETTLEMENT, so a green pill on a
 * money row would read as "this parent paid" — the opposite of what a reminder
 * says. Cobalt is the informational role, and the solid cobalt border keeps it
 * apart from the class-code chip on the same card, which is the same wash over
 * a grey border.
 *
 * A sent reminder outranks a drafted one, and the two do not merely differ in
 * words: "Drafted" is the honest reading of a message that only ever reached the
 * clipboard, so it gets the neutral outline every other not-yet-real state on
 * this surface wears, against the sent pill's fill.
 */
export const ReminderStateChip: React.FC<{
  remindedAt?: string | null;
  reminderCopiedAt?: string | null;
}> = ({ remindedAt, reminderCopiedAt }) => {
  const reminded = formatRelativeDays(remindedAt);
  if (reminded) {
    return (
      <Chip
        size="small"
        label={`Reminded ${reminded}`}
        sx={{
          bgcolor: C.cobaltWash,
          border: `1px solid ${C.cobalt}`,
          color: C.ink,
          fontWeight: 700,
        }}
      />
    );
  }
  const drafted = formatRelativeDays(reminderCopiedAt);
  if (!drafted) return null;
  return (
    <Chip
      size="small"
      label={`Drafted ${drafted}`}
      sx={{
        bgcolor: 'transparent',
        border: `1px solid ${C.border}`,
        color: C.inkMuted,
        fontWeight: 700,
      }}
    />
  );
};

// ---------------------------------------------------------------------------
// Cycle dots
// ---------------------------------------------------------------------------

/**
 * "n of 4" as filled dots. `used` may exceed `size` when a student has attended
 * beyond what they paid for — the overflow renders as amber-washed dots rather
 * than being clipped, because those are exactly the sessions the next payment
 * will be spent on.
 */
export const CycleDots: React.FC<{ used: number; size: number }> = ({
  used,
  size,
}) => {
  const total = Math.max(size, used);
  return (
    <Box
      sx={{ display: 'inline-flex', gap: 0.5, alignItems: 'center' }}
      role="img"
      aria-label={`${used} of ${size} sessions used`}
    >
      {Array.from({ length: total }, (_, index) => {
        const filled = index < used;
        const overflow = index >= size;
        return (
          <Box
            key={index}
            aria-hidden
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: overflow
                ? AMBER.strongBorder
                : filled
                  ? C.cobalt
                  : 'transparent',
              border: filled ? 'none' : `1px solid ${C.border}`,
            }}
          />
        );
      })}
    </Box>
  );
};

/** "3 of 4 · through Sat 23 Aug" — the paid-through reading from §11. */
export function paidThroughLabel(cycle: {
  cyclePosition: { used: number; size: number };
  paidThroughDate: string | null;
  paidThroughIsProjected: boolean;
}): string {
  const position = `${cycle.cyclePosition.used} of ${cycle.cyclePosition.size}`;
  if (!cycle.paidThroughDate) return position;
  const through = formatSessionDate(cycle.paidThroughDate);
  return cycle.paidThroughIsProjected
    ? `${position} · through ${through} (projected)`
    : `${position} · through ${through}`;
}
